import * as React from 'react'
import { AliceCardFragment, useAnalytics } from '@alice-financial/alice-sdk'
import { useIssuingElementsConfig } from './useIssuingElementsConfig'
import { useBetaIssuingElementsStripe } from '../../../../externalServices/stripe/useLoadStripe'

type UseWalletButtonParams = {
  card: AliceCardFragment
  stripeAccount: string | null | undefined
}

/**
 * Hook that creates and configures the Stripe "Add to Apple Wallet" button.
 * 
 * This hook handles:
 * - Loading Stripe.js with beta features
 * - Creating ephemeral key configuration
 * - Creating the Stripe button element
 * - Attaching analytics tracking
 * - Returning a mount function for the button
 * 
 * @param card - The Alice card to provision to the wallet
 * @param stripeAccount - The Stripe connected account ID
 * @returns Object with mountButton function and loading state
 */
export const useWalletButton = ({ card, stripeAccount }: UseWalletButtonParams) => {
  const hasStripeAccount = Boolean(stripeAccount)

  const issuingElementsStripe = useBetaIssuingElementsStripe({ stripeAccount })
  // Only fetch config when we have a valid stripeAccount to avoid rejected promise errors
  const issuingConfig = useIssuingElementsConfig(card, issuingElementsStripe, true, hasStripeAccount)
  const analytics = useAnalytics()

  const mountButton = React.useMemo(() => {
    if (!issuingConfig || !issuingConfig.issuingCard) {
      return undefined
    }

    const config = { ...issuingConfig, ...{ wallet: 'apple', buttonHeight: 40 } }

    const getElements = issuingElementsStripe.then(({ elements }) => elements)
    return getElements
      .then((elements) => {
        // @ts-expect-error issuingAddToWalletButton is in beta
        const button = elements.create('issuingAddToWalletButton', config)
        button.on('click', () => {
          analytics.track('alice_cards.stripe_wpp.clicked')
        })
        // Stripe beta docs say this is available, but actual types say it is not. In any case,
        // attempting to attach this handler does not cause a runtime error, so let's try it.
        // @ts-expect-error issuingAddToWalletButton is in beta
        button.on('success', () => {
          analytics.track('alice_cards.stripe_wpp.success')
        })
        return button
      })
      .then((element) => element.mount)
  }, [issuingElementsStripe, issuingConfig, analytics])

  return {
    mountButton,
    isLoading: !issuingConfig || !issuingConfig.issuingCard,
  }
}

